export const personalInfo = {
  name: "Chirag Patil",
  title: "Software Engineer",
  email: "chiragpatil2392@gmail.com",
  phone: "+1 (857) 506-4628",
  location: "Boston, MA",
  github: "https://github.com/patilchirag2392",
  linkedin: "https://www.linkedin.com/in/chiragp2392",
  website: "https://chiragup.foo",
  resume: "/resume.pdf",
  availability: "Open to full-time software engineering roles starting January 2027.",
  bio: {
    short:
      "Software engineer working across full-stack web, real-time systems, and iOS. Currently finishing an MS in Information Systems at Northeastern University.",
    detailed: [
      "I ship production features end to end, from React and Next.js frontends down to the data model underneath them. At Dyspute.ai I delivered 8 or more production features as one of four engineers on a Convex and TypeScript stack, building an automated case-notification system, extending the mediator dashboard, and running a zero-downtime ownership migration against live production data.",
      "Before that I worked in iOS at Crompton Greaves, writing Swift and UIKit for a hardware QA app that talked to pumps over MQTT. That mix stuck with me, and most of what I build since sits somewhere between systems work and product work: a hand-rolled sequence CRDT behind a collaborative editor, a WebSocket audio pipeline for live sentiment analysis, a language model squeezed onto a phone so an app can run with no network at all.",
      "I am pursuing a Master of Science in Information Systems at Northeastern University, graduating December 2026. Outside of work I play Rocket League and prototype whatever the next side project turns out to be."
    ]
  },
  roles: ["Full-stack web", "Real-time systems", "iOS"]
};

export const education = [
  {
    id: 1,
    school: "Northeastern University",
    degree: "Master of Science in Information Systems",
    location: "Boston, MA",
    duration: "Sep 2024 to Dec 2026"
  },
  {
    id: 2,
    school: "Veermata Jijabai Technological Institute (VJTI)",
    degree: "Bachelor of Technology in Electronics Engineering",
    location: "Mumbai, India",
    duration: "Jan 2021 to Jul 2024"
  }
];

export const experience = [
  {
    id: 1,
    slug: "dyspute-ai",
    role: "Software Engineer Co-op",
    company: "Dyspute.ai",
    location: "Boston, MA",
    duration: "Jan 2026 to May 2026",
    stack: ["React", "Next.js", "TypeScript", "Convex"],
    description: [
      "Delivered 8 or more production features as one of 4 engineers, roughly 143 commits over a 4-month co-op, including a case-notification system with mediator milestone emails, party nudges over email and SMS, and 72-hour and 7-day inactivity reminders.",
      "Extended the case-management dashboard with an activity feed, external case IDs, an always-on assignee column, and deadline and idle warning indicators, surfacing overdue and stalled cases at a glance.",
      "Consolidated case assignment onto a unified ownership model and retired the legacy assignment field across all queries, mutations, and UI consumers in a zero-downtime, backwards-compatible migration on live data.",
      "Hardened onboarding and authentication flows with an optional phone number, Unicode-aware name validation, a shared account-type picker for Google sign-in, and broadened public-route handling, reducing signup friction.",
      "Improved transactional email deliverability by serving image assets from the company's own domain to bypass spam filters, and added case archive and restore supporting any case status."
    ]
  },
  {
    id: 2,
    slug: "crompton",
    role: "Software Development Intern",
    company: "Crompton Greaves Consumer Electricals",
    location: "Mumbai, India",
    duration: "Jun 2023 to Dec 2023",
    stack: ["Swift", "UIKit", "MQTT", "REST"],
    description: [
      "Built production UI in Swift and UIKit for the Crompton Senze iOS app, used by hardware QA engineers to validate the Armor Pump IoT product line.",
      "Implemented bidirectional MQTT communication for real-time telemetry streaming and remote pump configuration.",
      "Designed REST endpoints for token-based authentication and role-based access control, letting authorized testers modify advanced device settings remotely."
    ]
  }
];

export const projects = [
  {
    id: 1,
    slug: "converge",
    title: "Converge",
    date: "Aug 2026",
    summary:
      "Real-time collaborative document editor backed by a sequence CRDT written from scratch.",
    longDescription:
      "Built a real-time collaborative document editor where multiple clients edit concurrently over WebSockets, backed by a custom RGA-style sequence CRDT with no third-party library for conflict-free merges and PostgreSQL persistence. Scaled real-time sync across server instances with Redis pub/sub, then containerized the stack with Docker behind an Nginx reverse proxy and shipped it on AWS through a GitHub Actions CI/CD pipeline.",
    technologies: [
      "React",
      "TypeScript",
      "FastAPI",
      "WebSockets",
      "PostgreSQL",
      "Redis",
      "Docker",
      "Nginx",
      "AWS"
    ],
    features: [
      "Custom RGA-style sequence CRDT, no third-party library.",
      "Concurrent multi-client editing over WebSockets with conflict-free merges.",
      "PostgreSQL persistence with Redis pub/sub fanning sync across instances.",
      "Dockerized stack behind an Nginx reverse proxy.",
      "Deployed on AWS through a GitHub Actions CI/CD pipeline."
    ],
    links: { github: "https://github.com/patilchirag2392/converge" }
  },
  {
    id: 2,
    slug: "sentiment-aura",
    title: "Sentiment Aura",
    date: "Nov 2025",
    summary:
      "Live speech-to-text with real-time sentiment analysis driving a generative visualization.",
    longDescription:
      "Built a full-stack web app that performs live speech-to-text transcription and real-time sentiment analysis on streaming microphone audio, driving a generative art visualization that reacts to the detected emotion. Engineered a FastAPI WebSocket proxy to broker Deepgram streaming auth from the browser, processing 16 kHz PCM audio through a custom AudioWorklet that replaced the deprecated ScriptProcessorNode API, and streamed finalized transcript segments to the Claude API for sentiment, intensity, and keyword classification.",
    technologies: [
      "React",
      "FastAPI",
      "WebSockets",
      "Deepgram",
      "Anthropic Claude API",
      "react-p5"
    ],
    features: [
      "Live speech-to-text transcription on streaming microphone audio.",
      "FastAPI WebSocket proxy brokering Deepgram streaming auth from the browser.",
      "Custom AudioWorklet processing 16 kHz PCM audio in place of ScriptProcessorNode.",
      "Claude API classification of sentiment, intensity, and keywords returned over the same WebSocket channel.",
      "Generative Perlin-noise flow field driven by the classified sentiment."
    ],
    links: { github: "https://github.com/patilchirag2392/Sentiment-Aura" }
  },
  {
    id: 3,
    slug: "preppal",
    title: "PrepPal",
    date: "Apr 2025",
    summary:
      "iOS meal planner running a local language model for fully offline, on-device generation.",
    longDescription:
      "Built an iOS meal-planning app that runs a local Ollama LLM for fully offline, on-device meal-plan generation at zero cloud inference cost, keeping all user data on the device. Implemented multi-device sync and shared grocery lists with Firebase Auth and Firestore over an offline-first cache, architected in MVVM with Core Data for local persistence.",
    technologies: ["Swift", "SwiftUI", "MVVM", "Firebase", "Ollama"],
    features: [
      "Fully offline, on-device meal-plan generation through a local Ollama LLM.",
      "Zero cloud inference cost, with user data kept on the device.",
      "Multi-device sync and shared grocery lists with Firebase Auth and Firestore.",
      "Offline-first cache architected in MVVM with Core Data for local persistence."
    ],
    links: { github: "https://github.com/patilchirag2392/PrepPal" }
  },
  {
    id: 4,
    slug: "tonesafe",
    title: "ToneSafe",
    date: "Swift Student Challenge 2026",
    summary:
      "On-device Core ML classifier that flags harmful tone before a message is sent.",
    longDescription:
      "Built an iOS app that flags harmful or aggressive tone before a message is sent, scoring it across multiple tone categories with an on-device Core ML text classifier so no message text ever leaves the phone. Submitted to the Apple Swift Student Challenge 2026.",
    technologies: ["Swift", "SwiftUI", "Core ML"],
    features: [
      "On-device Core ML text classification, so no message text leaves the phone.",
      "Scoring across multiple tone categories with per-message feedback before sending.",
      "SwiftUI interface for analysis results and message history.",
      "Submitted to the Apple Swift Student Challenge 2026."
    ],
    links: { github: "https://github.com/patilchirag2392/ToneSafe" }
  }
];

export const skills = [
  {
    id: "languages",
    label: "Languages",
    items: [
      "Python",
      "JavaScript",
      "TypeScript",
      "Swift",
      "C++",
      "Java",
      "Dart",
      "SQL",
      "HTML",
      "CSS"
    ]
  },
  {
    id: "web-backend",
    label: "Web and Backend",
    items: [
      "React",
      "Next.js",
      "Node.js",
      "Express",
      "FastAPI",
      "Convex",
      "REST APIs",
      "WebSockets",
      "JWT",
      "OAuth 2.0"
    ]
  },
  {
    id: "ios-mobile",
    label: "iOS and Mobile",
    items: [
      "SwiftUI",
      "UIKit",
      "Combine",
      "Core Data",
      "Core ML",
      "XCTest",
      "MVVM",
      "Flutter",
      "React Native"
    ]
  },
  {
    id: "data-cloud",
    label: "Data, Cloud, DevOps",
    items: [
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Redis",
      "Firebase",
      "AWS",
      "Docker",
      "Nginx",
      "GitHub Actions"
    ]
  },
  {
    id: "tools",
    label: "Tools",
    items: [
      "Git",
      "GitHub",
      "Xcode",
      "VS Code",
      "IntelliJ",
      "Postman",
      "Figma",
      "Linux",
      "Agile/Scrum"
    ]
  }
];

export const certifications = [
  {
    id: 1,
    title: "Apple Swift Student Challenge 2026",
    detail: "Submitted iOS app, ToneSafe."
  },
  {
    id: 2,
    title: "Introduction to iOS Mobile Application Development",
    detail: "Meta, 2024."
  },
  {
    id: 3,
    title: "Google Developer Student Clubs, VJTI",
    detail:
      "Flutter Core Team Member, Aug 2022 to May 2024. Led app-development workshops including the Flutter Forward event organized by Google."
  }
];
// Highlights on the intro. Every line restates something on the resume.
export const highlights = [
  "8 or more production features shipped as one of 4 engineers at Dyspute.ai, roughly 143 commits in 4 months",
  "Zero-downtime, backwards-compatible ownership migration on live production data",
  "Sequence CRDT written from scratch for concurrent editing, no third-party library",
  "Streaming audio over WebSockets: 16 kHz PCM through a custom AudioWorklet",
  "On-device ML on iOS: Core ML classification and a local LLM at zero cloud inference cost",
  "Bidirectional MQTT telemetry and remote configuration for IoT pump hardware",
  "Full delivery path: Docker, Nginx, AWS, and GitHub Actions CI/CD"
];
